export interface PlanCatalogOption {
  systemOptionId: number;
  strategyKey: string;
  panels: number;
}

export interface SelectedPlanOptions<T extends PlanCatalogOption> {
  /** Closest panel count to the roof total. Ties use the smaller system. */
  good: T;
  /** The catalog row whose strategy key is `balanced`, when one exists. */
  better: T | null;
  /** Unused option closest to the midpoint of the first two panel counts. */
  best: T;
}

export function selectPlanOptions<T extends PlanCatalogOption>(
  catalog: T[],
  totalPanels: number,
): SelectedPlanOptions<T> | null {
  const sized = catalog.filter((option) => option.panels > 0 && Number.isFinite(option.panels));
  if (sized.length === 0 || !Number.isFinite(totalPanels) || totalPanels <= 0) {
    return null;
  }

  const recommended = closestByPanels(sized, totalPanels);
  const balanced = sized.find((option) => option.strategyKey === "balanced") ?? null;
  const balancedPanels = balanced?.panels ?? recommended.panels;
  const midpoint = (recommended.panels + balancedPanels) / 2;
  const used = new Set(
    [recommended.systemOptionId, balanced?.systemOptionId].filter((id): id is number => id != null),
  );
  const unused = sized.filter((option) => !used.has(option.systemOptionId));
  const bestValue = closestByPanels(unused.length > 0 ? unused : sized, midpoint);

  return {
    good: recommended,
    better: balanced,
    best: bestValue,
  };
}

function closestByPanels<T extends PlanCatalogOption>(options: T[], target: number): T {
  return options.slice().sort((left, right) => {
    const distance = Math.abs(left.panels - target) - Math.abs(right.panels - target);
    if (distance !== 0) return distance;
    return left.panels - right.panels;
  })[0];
}
